import {Page} from '@/interfaces/server-data';
import {FindFiltersParams} from '@/interfaces/global-data';
import {ImportUploadOption, IUpdateOmnichannelStrategiesTask} from '@/interfaces/server-data';

import {
  IOmnichannelFindMissedStoreParamsRequest,
  IOmnichannelMissedStoreParamsResponse,
  IOmnichannelStrategyIntersections,
  IOmnichannelStrategyQueryDto,
  IOmnichannelStrategySaveDto,
  IOmnichannelStrategyTableDto,
} from '@/pages/omnichannel-strategy/types/data';
import {
  IProductListItem,
  IProductListFilter,
  IProductListQueryParams,
} from '@/pages/omnichannel-strategy/types/product-list';
import {AxiosPromise} from 'axios';
import {Component, Mixins} from 'vue-property-decorator';
import toast from '@/mixins/toast';

@Component
export default class OmnichannelStrategyApi extends Mixins(toast) {
  private basePath = '/api/omnichannel-strategies';
  private productListPath = '/api/filters';
  private errorListPath = '/api/error/list';

  fetchStrategies(query: IOmnichannelStrategyQueryDto): AxiosPromise<Page<IOmnichannelStrategyTableDto[]>> {
    return this.$http.post(`${this.basePath}/list`, query);
  }

  validateIntersections(strategyParams: IOmnichannelStrategySaveDto): AxiosPromise<IOmnichannelStrategyIntersections> {
    return this.$http.post(`${this.basePath}/intersections`, strategyParams)
  }

  saveStrategy(strategyParams: IOmnichannelStrategySaveDto): AxiosPromise<IOmnichannelStrategySaveDto> {
    if (strategyParams.id != null && strategyParams.id > 0) {
      const auth = localStorage.getItem('AUTH');
      if (!auth) return Promise.reject();

      const { user_id: userId } = JSON.parse(auth);

      return this.$http.put(`${this.basePath}/${strategyParams.id}`, strategyParams, { params: { userId } });
    } else {
      return this.$http.post(this.basePath, strategyParams);
    }
  }

  loadStrategy(id: number): AxiosPromise<IOmnichannelStrategySaveDto> {
    const auth = localStorage.getItem('AUTH');
    if (!auth) return Promise.reject();

    const { user_id: userId } = JSON.parse(auth);

    return this.$http.get(`${this.basePath}/${id}`, { params: { userId } })
  }

  deleteStrategy(id: number): AxiosPromise<any> {
    const auth = localStorage.getItem('AUTH');
    if (!auth) return Promise.reject();

    const { user_id: userId } = JSON.parse(auth);

    return this.$http.delete(`${this.basePath}/${id}`, { params: { userId } })
  }

  findMissedStoreParams(request: IOmnichannelFindMissedStoreParamsRequest): AxiosPromise<IOmnichannelMissedStoreParamsResponse> {
    return this.$http.post(`${this.basePath}/find-missed-params`, request)
  }

  fetchProductList(
      filter: IProductListFilter,
      params: IProductListQueryParams,
  ): AxiosPromise<Page<IProductListItem[]>> {
    return this.$http.post(`${this.productListPath}/product-list`, filter, {
      params: {
        limit: params.limit,
        offset: params.offset
      },
    });
  }

  downloadProductListExcel(filter: FindFiltersParams): AxiosPromise<Blob> {
    return this.$http.post(`${this.productListPath}/product-list-download`, filter, {
      responseType: 'blob',
    });
  }

  uploadOmnichannelStrategiesFile(
    tradingId: number,
    uploadFile: FormData,
    uploadOption: ImportUploadOption
  ): AxiosPromise<string> {
    return this.$http.post(`${this.basePath}/upload-omnichannel-strategies-file`, uploadFile, {
      headers: { 'content-type': 'multipart/form-data' },
      params: { tradingId, uploadOption },
    });
  }

  uploadOmnichannelStrategiesIndividualFile(
    tradingId: number,
    uploadFile: FormData
  ): AxiosPromise<string> {
    return this.$http.post(`${this.basePath}/upload-omnichannel-strategies-individual-file`, uploadFile, {
      headers: { 'content-type': 'multipart/form-data' },
      params: { tradingId },
    });
  }

  getLastFileErrorsAmount(tradingId: number, fileType: string): AxiosPromise<number> {
    return this.$http.get(`${this.errorListPath}/files/last/amount`, {
      params: { tradingId, fileType },
    }).catch((e) => {
      this.showError(this.$t('omnichannelStrategy.messages.getErrorsAmountFailedApi') as string);
      throw e;
    });
  }

  getOmnichannelStrategiesImportErrors(tradingId: number): AxiosPromise<any> {
    return this.$http.get(`${this.errorListPath}/omnichannel-strategies-import`, {
      params: { tradingId },
    }).catch((e) => {
      this.showError(this.$t('omnichannelStrategy.messages.getErrorLogFailedApi') as string);
      throw e;
    });
  }

  getOmnichannelStrategiesIndividualImportErrors(tradingId: number): AxiosPromise<any> {
    return this.$http.get(`${this.errorListPath}/omnichannel-strategies-individual-import`, {
      params: { tradingId },
    }).catch((e) => {
      this.showError(this.$t('omnichannelStrategy.messages.individualImportErrorsFailedApi') as string);
      throw e;
    });
  }

  exportOmnichannelStrategies(tradingId: number, userId: number): AxiosPromise<Blob> {
    return this.$http.get(`${this.basePath}/export`, {
      params: { tradingId, userId },
      responseType: 'blob',
    }).catch((e) => {
      this.showError(this.$t('omnichannelStrategy.messages.exportFailedApi') as string);
      throw e;
    });
  }

  getOmnichannelStrategiesUploadStatus(tradingId: number): AxiosPromise<IUpdateOmnichannelStrategiesTask> {
    return this.$http.get(`${this.basePath}/upload-status`, { params: { tradingId } })
      .catch((e) => {
        this.showError(this.$t('omnichannelStrategy.messages.uploadStatusFailedApi') as string);
        throw e;
      });
  }

  getOmnichannelStrategiesIndividualUploadStatus(tradingId: number): AxiosPromise<IUpdateOmnichannelStrategiesTask> {
    return this.$http.get(`${this.basePath}/upload-individual-status`, { params: { tradingId } })
      .catch((e) => {
        this.showError(this.$t('omnichannelStrategy.messages.uploadStatusFailedApi') as string);
        throw e;
      });
  }
}
